import React from 'react';

import { gql } from '@apollo/client';
import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments } from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import { Alert, Button, Stack, Typography } from '@mui/material';

import {
  RemoveMemberModal_InterviewModuleMemberFragment,
  useInterviewModuleDeleteMemberMutation,
} from 'src/generated/mloop-graphql';

import { resetModuleMemberListCache } from 'src/entities/InterviewModuleMember/utils';

import { apolloCacheDeleteById } from 'src/utils/apolloHelpers';

type Props = {
  open: boolean;
  onDelete: () => void;
  onClose: () => void;
  onPauseClick: () => void;
};

type Fragments = {
  interviewModuleMember: RemoveMemberModal_InterviewModuleMemberFragment;
};

const RemoveMemberModal: FCWithFragments<Fragments, Props> = ({
  open,
  onClose,
  onDelete,
  onPauseClick,
  interviewModuleMember,
}) => {
  const { t } = useTranslation();
  const [deleteMembers, { loading: removing, error }] = useInterviewModuleDeleteMemberMutation();

  const handleRemove = async () => {
    try {
      await deleteMembers({
        variables: {
          input: {
            employeeIds: [interviewModuleMember.employeeId],
            interviewModuleId: interviewModuleMember.interviewModuleId,
          },
        },
        update(cache) {
          apolloCacheDeleteById(cache, interviewModuleMember.interviewModuleId, 'InterviewModule');
          resetModuleMemberListCache({
            cache,
            moduleId: interviewModuleMember.interviewModuleId,
            employeeIds: [interviewModuleMember.employeeId],
          });
        },
      });
      onDelete();
    } catch (e) {
      console.error(e);
    }
  };

  if (interviewModuleMember.stats?.upcoming) {
    return (
      <Dialog
        cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
        submitOptions={{
          label: t('hiring-dashboard-common:common.okay'),
          onClick: onClose,
        }}
        title={t('hiring-dashboard-interviewers:interviewers.removeMember.cannotRemoveTitle')}
        open={open}
        onClose={onClose}
      >
        <Stack direction="column" spacing={1}>
          <Typography>{t('hiring-dashboard-interviewers:interviewers.removeMember.scheduledBody')}</Typography>
          <Typography>{t('hiring-dashboard-interviewers:interviewers.removeMember.updateUpcomingBody')}</Typography>
          <Alert variant="outlined" color="info" severity="info">
            <Stack alignItems="flex-start">
              <Typography>
                {t('hiring-dashboard-interviewers:interviewers.removeMember.temporaryBreakPrompt')}
              </Typography>
              <Button size="medium" variant="text" color="primary" onClick={onPauseClick}>
                {t('hiring-dashboard-interviewers:interviewers.removeMember.pauseInstead')}
              </Button>
            </Stack>
          </Alert>
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
      submitOptions={{
        label: t('hiring-dashboard-interviewers:interviewers.removeMember.remove'),
        onClick: handleRemove,
        isDisabled: removing,
        isLoading: removing,
      }}
      title={t('hiring-dashboard-interviewers:interviewers.removeMember.title')}
      open={open}
      onClose={onClose}
    >
      <Stack direction="column" spacing={1}>
        <Typography>
          <Trans i18nKey="hiring-dashboard-interviewers:interviewers.removeMember.removeBody">
            This interviewer will no longer be eligible for scheduling as part of this module. Records of their
            interviews will be preserved, but <b>any training history will be lost</b>.
          </Trans>
        </Typography>
        <Alert variant="outlined" color="info" severity="info">
          <Stack alignItems="flex-start">
            <Typography>{t('hiring-dashboard-interviewers:interviewers.removeMember.temporaryBreakPrompt')}</Typography>
            <Button size="medium" variant="text" color="primary" onClick={onPauseClick}>
              {t('hiring-dashboard-interviewers:interviewers.removeMember.pauseInstead')}
            </Button>
          </Stack>
        </Alert>
      </Stack>

      <PublicError error={error} />
    </Dialog>
  );
};

RemoveMemberModal.fragments = {
  interviewModuleMember: gql`
    fragment RemoveMemberModal_interviewModuleMember on InterviewModuleMember {
      employeeId
      interviewModuleId
      stats {
        upcoming
      }
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InterviewModuleDeleteMemberMutation = gql`
  mutation InterviewModuleDeleteMember($input: InterviewModuleRemoveMembersInput!) {
    interviewModuleRemoveMembers(input: $input) {
      interviewModule {
        id
      }
    }
  }
`;

export default RemoveMemberModal;
